import { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { BodyParameters } from '@/enums/bodyParameters'
import { Buttons } from '@/enums/buttons'

import { PrimaryButton, ButtonSpinner } from '@/components/buttons/primaryButton'
import { MeasurementSection } from '@/components/measurementSection'
import { PageTitle } from '@/components/pageTitle'
import { FooterPolicy } from '@/components/policyLinkBlock'
import { TextBlock } from '@/components/textBlock'

import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'
import { trackPageView, trackPageComplete } from '@/events/events'

import { TBodyParamsProps } from './types'
import { MeasurementInput } from '../pageComponents'
import { convertInchesToCm } from '../utils'

export const HeightPage = ({
  pageName,
  pageConfig,
  setLastVisitedItem,
  setItem,
  getItem,
  config,
}: TBodyParamsProps) => {
  const { user } = useAuth()
  const sessionContext = useSession()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [cm, setSm] = useState<string>('')
  const [feet, setFeet] = useState<string>('')
  const [inches, setInches] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [measurementSystem, setMeasurementSystem] = useState<BodyParameters>(BodyParameters.FT)
  const value = cm || feet
  const navigate = useNavigate()
  const {
    title,
    nextUrl,
    buttonName,
    policyBlock = false,
    isShowError = true,
    textBlocks,
  } = pageConfig
  const isFT = measurementSystem === BodyParameters.FT

  const isFeetButtonDisabled = isLoading || (isFT && !feet)

  const isCmButtonDisabled = isLoading || (!isFT && !cm)

  useEffect(() => {
    const pageData = getItem(pageName)
    if (pageData) {
      const heightParams = JSON.parse(pageData)
      if (heightParams.type === 'imperial') {
        validateImperial(heightParams.quantity.feet, heightParams.quantity.inches)
        setFeet(heightParams.quantity.feet)
        setInches(heightParams.quantity.inches)
      }
      if (heightParams.type === 'metric') {
        setSm(heightParams.quantity.cm)
      }
    }
  }, [isFT, pageName, getItem])

  useEffect(
    () => {
      trackPageView(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        sessionContext,
        user
      )
      document.title = title
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    const pageData = getItem(pageName)
    if (pageData) {
      const heightParams = JSON.parse(pageData)
      setMeasurementSystem(heightParams.type === 'imperial' ? BodyParameters.FT : BodyParameters.CM)
    }
  }, [getItem, pageName, title, config, user])

  const changeMeasurement = (value: BodyParameters) => () => {
    setMeasurementSystem(value)
  }

  const changeCM = (event: ChangeEvent<HTMLInputElement>) => {
    setSm(event.currentTarget.value)

    if (parseInt(event?.currentTarget?.value) < 130) {
      setError('Please, enter a value above 130 cm')
      return
    }
    setError(null)
  }

  function validateImperial(feet: number, inches: number) {
    if (isNaN(feet) || isNaN(inches)) {
      setError('Please, enter a value above 4 ft 3 in')
      return
    }
    if (feet < 4) {
      setError('Please, enter a value above 4 ft 3 in')
      return
    } else if (feet == 4 && inches < 4) {
      setError('Please, enter a value above 4 ft 3 in')
      return
    }

    setError(null)
  }

  const changeFeet = (event: ChangeEvent<HTMLInputElement>) => {
    if (event?.currentTarget?.value.length >= 2) {
      return
    }
    console.log('feet:', event.currentTarget.value, 'inches:', inches)
    setFeet(event.currentTarget.value)
    validateImperial(parseInt(event.currentTarget.value), parseInt(inches))
  }

  const changeInches = (event: ChangeEvent<HTMLInputElement>) => {
    setInches(event.currentTarget.value)

    validateImperial(parseInt(feet), parseInt(event.currentTarget.value))
  }

  const nextPage = async (e?: FormEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    setIsLoading(true)

    const inc = inches ? inches : '0'
    if (!inches) {
      setInches('0')
    }

    const answerInCm =
      measurementSystem === BodyParameters.FT ? convertInchesToCm(+feet, +inches) : cm

    trackPageComplete(
      pageName,
      [answerInCm.toString()],
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      sessionContext,
      user
    )

    navigate(nextUrl)
    const params = {
      type: measurementSystem === BodyParameters.FT ? 'imperial' : 'metric',
      quantity: isFT ? { feet, inches: inc } : { cm },
    }
    setItem(pageName, JSON.stringify(params))
    setLastVisitedItem(pageName)
  }

  const className = `page page-survey page-measurements ${pageName}`

  const formDisabled = Boolean(error) || isCmButtonDisabled || isFeetButtonDisabled

  return (
    <div className={className}>
      <PageTitle title={title} />
      <MeasurementSection
        changeLeftMeasurement={changeMeasurement(BodyParameters.CM)}
        changeRightMeasurement={changeMeasurement(BodyParameters.FT)}
        isActiveLeft={!isFT}
        leftName={BodyParameters.CM}
        rightName={BodyParameters.FT}
      />
      {measurementSystem === BodyParameters.CM && (
        <form onSubmit={nextPage}>
          <MeasurementInput
            maxLength={3}
            size={3}
            type={'number'}
            value={cm}
            onChange={changeCM}
            unit={BodyParameters.CM}
          />
        </form>
      )}

      {measurementSystem === BodyParameters.FT && (
        <form className="inputs-container" onSubmit={nextPage}>
          <MeasurementInput
            maxLength={1}
            type={'number'}
            value={feet}
            onChange={changeFeet}
            unit={BodyParameters.FT}
            disabled={isLoading}
          />
          <MeasurementInput
            maxLength={2}
            type={'number'}
            value={inches}
            onChange={changeInches}
            unit="in"
            disabled={isLoading}
          />
          {/* input with submit required to make submit on Enter work */}
          {/* in forms with multiple inputs */}
          <input type="submit" hidden />
        </form>
      )}
      {isShowError && <div className="error-message">{error}</div>}
      {textBlocks &&
        value &&
        textBlocks.map(({ text, ...blockProps }) => {
          text = typeof text === 'function' ? text() : text
          return <TextBlock key={text} text={text} {...blockProps} />
        })}
      <div className="button-container">
        <PrimaryButton onClick={nextPage} disabled={formDisabled}>
          {isLoading ? <ButtonSpinner /> : buttonName || Buttons.NEXT}
        </PrimaryButton>
      </div>
      {policyBlock && <FooterPolicy />}
    </div>
  )
}
